import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/manga';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleCategoryClick = useCallback((slug: string) => {
    navigate(`/category/${slug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [navigate]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <div className="mx-auto flex items-center justify-between px-4 md:px-6 h-16">
        <Link to="/" className="flex items-center gap-2" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
          <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
            <i className="ri-book-open-line text-background-50 text-lg"></i>
          </div>
          <span className="text-lg font-bold text-foreground-950 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>韩漫之家</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${isActive('/') ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'}`}>首页</Link>
          <div className="relative group">
            <button className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1 ${location.pathname.startsWith('/category') ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'}`}>
              分类浏览
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-2 grid grid-cols-2 gap-1 min-w-[200px]">
                {categories.filter(c => c.slug !== 'all').map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.slug)}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer text-left"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Link to="/about" className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${isActive('/about') ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'}`}>关于我们</Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
            <i className="ri-search-line text-foreground-600"></i>
          </button>
          <Link to="/" className="px-5 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">开始阅读</Link>
        </div>

        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden w-9 h-9 flex items-center justify-center rounded-full bg-background-100 cursor-pointer">
          <i className={`text-foreground-600 text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <div className="flex flex-col gap-3">
            <Link to="/" className="text-sm font-medium text-foreground-700 py-2">首页</Link>
            <p className="text-sm font-medium text-foreground-500 py-1">分类浏览</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.filter(c => c.slug !== 'all').map((cat) => (
                <button key={cat.slug} onClick={() => handleCategoryClick(cat.slug)} className="flex items-center gap-2 px-2 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer">
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
            <Link to="/about" className="text-sm font-medium text-foreground-700 py-2">关于我们</Link>
          </div>
        </div>
      )}
    </nav>
  );
}